import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';

export const Header = () => {
  const { language, toggleLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { key: 'products', href: '#products' },
    { key: 'features', href: '#features' },
    { key: 'howItWorks', href: '#how-it-works' },
    { key: 'contact', href: '#contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 theme-transition"
      data-testid="header"
    >
      <div className="backdrop-blur-xl bg-white/80 dark:bg-slate-950/80 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="section-container">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a 
              href="#" 
              className="flex items-center gap-2"
              data-testid="logo"
            >
              {/* A icon */}
              <img 
                src="https://customer-assets.emergentagent.com/job_platform-control-9/artifacts/v13sl9x3_Ads%C4%B1z%20tasar%C4%B1m%20%2815%29.png"
                alt="AgrosTech"
                className="w-10 h-10 object-contain dark:drop-shadow-[0_0_8px_rgba(255,255,255,0.25)]"
              />
              {/* Text */}
              <span className="text-xl font-bold tracking-tight">
                <span className="text-slate-900 dark:text-white">Agros</span>
                <span className="text-indigo-600 dark:text-indigo-400">Tech</span>
              </span>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  data-testid={`nav-${item.key}`}
                >
                  {translations.nav[item.key][language]}
                </a>
              ))}
            </nav>

            {/* Language Toggle & CTA */}
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
                data-testid="language-toggle"
              >
                <Globe className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm font-medium uppercase">{language}</span>
              </Button>

              <a
                href="#contact"
                onClick={(e) => scrollToSection(e, '#contact')}
                className="hidden md:inline-flex"
              >
                <Button
                  className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 shadow-lg shadow-indigo-500/20 hover:scale-105 transition-all"
                  data-testid="header-cta"
                >
                  {translations.hero.cta[language]}
                </Button>
              </a>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 text-slate-600 dark:text-slate-400"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" strokeWidth={1.5} />
                ) : (
                  <Menu className="w-6 h-6" strokeWidth={1.5} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200/50 dark:border-slate-800/50">
            <nav className="section-container py-4 flex flex-col gap-2">
              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className="py-3 px-4 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  data-testid={`mobile-nav-${item.key}`}
                >
                  {translations.nav[item.key][language]}
                </a>
              ))}
              <a
                href="#contact"
                onClick={(e) => scrollToSection(e, '#contact')}
                className="mt-2"
              >
                <Button
                  className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="mobile-cta"
                >
                  {translations.hero.cta[language]}
                </Button>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
